import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { toast } from "react-toastify";

import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "../../components/misc/Card";
import CustomToast from "../../components/misc/CustomToast";
import { getApiErrorMessage } from "../../utils/apiError";
import Spinner from "../../components/misc/Spinner";
import {
    Table,
    TableBody,
    TableCell,
    TableFooter,
    TableHead,
    TableHeader,
    TableRow,
} from "../../components/misc/Table";
import { cn } from "../../utils/classMerger";
import { useIsMobile } from "../../hooks/useIsMobile";
import { getCommercialAutoVideoReadiness } from "./actions";
import { CommercialAutoVideoReadiness } from "./types";

/**
 * MB-1473 — Commercial auto-video readiness.
 *
 * Auto-slideshow videos opened to all nine commercial categories at once,
 * behind a per-category flag in the api's COMMERCIAL_AUTO_VIDEO_CATEGORIES.
 * This page is how that flag is steered: it says, per category, how many
 * listings exist, how many the eligibility gate lets through, how many then
 * fall out under the image floor, and how many videos actually came out.
 *
 * The column that usually explains a disappointing row is "Under photo floor" —
 * a listing can be perfectly eligible and still never render because it does
 * not carry enough campaign-qualified photos. The median beside it is what that
 * floor should be judged against.
 */
const CommercialAutoVideo: React.FC = () => {
    const isMobile = useIsMobile(900);
    const [data, setData] = useState<CommercialAutoVideoReadiness | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        getCommercialAutoVideoReadiness()
            .then((result) => {
                if (!cancelled) setData(result);
            })
            .catch((error) =>
                toast.error(
                    <CustomToast
                        message={getApiErrorMessage(
                            error,
                            "Could not load commercial auto-video readiness"
                        )}
                    />
                )
            )
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const num = (n: number): string => n.toLocaleString();

    return (
        <div className={cn("flex flex-col gap-4", isMobile ? "p-3" : "p-6")}>
            <div className="flex flex-col gap-1">
                <h1 className="text-left text-xl font-bold">
                    Commercial auto-video readiness
                </h1>
                <p className="text-left text-sm text-muted-foreground">
                    Where commercial listings stand on the way to an
                    auto-generated slideshow video. Every listing sits in exactly
                    one of Ready, Under photo floor, or Not eligible.
                </p>
            </div>

            {loading && (
                <div className="flex justify-center py-16">
                    <Spinner />
                </div>
            )}

            {!loading && data && (
                <>
                    <div
                        className={cn(
                            "grid gap-4",
                            isMobile ? "grid-cols-2" : "grid-cols-5"
                        )}
                    >
                        <SummaryCard
                            title="Commercial listings"
                            value={num(data.totals.total)}
                            note="Across all nine categories"
                        />
                        <SummaryCard
                            title="Ready for a video"
                            value={num(data.totals.ready)}
                            note={`Eligible and at or above ${data.imageFloor} qualified photos`}
                        />
                        <SummaryCard
                            title="Under the photo floor"
                            value={num(data.totals.skippedForImages)}
                            note="Eligible, but not enough campaign-qualified photos"
                            // The one number worth colouring: these listings pass
                            // every rule and still cannot render, so a large value
                            // is an argument about the floor, not a defect.
                            emphasis={data.totals.skippedForImages > 0}
                        />
                        <SummaryCard
                            title="Tag mismatch"
                            value={num(data.totals.vocabularyMismatch)}
                            note="Enough photos, but their tags are outside the commercial vocabulary"
                            emphasis={data.totals.vocabularyMismatch > 0}
                        />
                        <SummaryCard
                            title="Videos generated"
                            value={num(data.totals.videosGenerated)}
                            note={`${num(
                                data.totals.videosCompleted
                            )} completed · ${num(
                                data.totals.listingsWithVideo
                            )} listings`}
                        />
                    </div>

                    <Card className="mt-0">
                        <CardHeader className="p-4 pb-2">
                            <CardTitle className="text-left text-sm font-semibold">
                                By category
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="p-0">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="text-left">
                                            Category
                                        </TableHead>
                                        <TableHead className="text-right">
                                            Listings
                                        </TableHead>
                                        <TableHead className="text-right">
                                            Eligible
                                        </TableHead>
                                        <TableHead className="text-right">
                                            Ready
                                        </TableHead>
                                        <TableHead className="text-right">
                                            Under photo floor
                                        </TableHead>
                                        <TableHead className="text-right">
                                            Not eligible
                                        </TableHead>
                                        <TableHead
                                            className="text-right"
                                            title="Counted in Ready, but the selector will still refuse them: too few of their photos carry a tag the commercial vocabulary can place. A tagging problem, not a photo-count problem."
                                        >
                                            Tag mismatch
                                        </TableHead>
                                        <TableHead className="text-right">
                                            Median photos
                                        </TableHead>
                                        <TableHead className="text-right">
                                            Videos
                                        </TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {data.categories.map((row) => (
                                        <TableRow key={row.category}>
                                            <TableCell className="text-left font-medium">
                                                <span className="flex items-center gap-2">
                                                    {row.label}
                                                    {!row.enabled && (
                                                        <span
                                                            title="Held back in COMMERCIAL_AUTO_VIDEO_CATEGORIES - these listings use the manual slideshow wizard"
                                                            className="cursor-help rounded-full bg-secondary px-2 py-0.5 text-[10px] font-bold text-primary"
                                                        >
                                                            Held back
                                                        </span>
                                                    )}
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {num(row.total)}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {num(row.eligible)}
                                            </TableCell>
                                            <TableCell className="text-right font-semibold">
                                                {num(row.ready)}
                                            </TableCell>
                                            <TableCell
                                                className={cn(
                                                    "text-right",
                                                    row.skippedForImages > 0 &&
                                                        "font-semibold text-[#fa9600]"
                                                )}
                                            >
                                                {num(row.skippedForImages)}
                                            </TableCell>
                                            <TableCell className="text-right text-muted-foreground">
                                                {num(row.ineligible)}
                                            </TableCell>
                                            <TableCell
                                                className={cn(
                                                    "text-right",
                                                    row.vocabularyMismatch > 0 &&
                                                        "font-semibold text-[#fa9600]"
                                                )}
                                            >
                                                {num(row.vocabularyMismatch)}
                                            </TableCell>
                                            <TableCell
                                                className={cn(
                                                    "text-right",
                                                    row.total > 0 &&
                                                        row.medianQualifiedImages <
                                                            data.imageFloor &&
                                                        "font-semibold text-[#fa9600]"
                                                )}
                                                title={`The floor is ${data.imageFloor} campaign-qualified photos`}
                                            >
                                                {row.total > 0
                                                    ? num(
                                                          row.medianQualifiedImages
                                                      )
                                                    : "-"}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {num(row.videosGenerated)}
                                                {row.videosGenerated > 0 && (
                                                    <span className="ml-1 text-xs text-muted-foreground">
                                                        ({num(
                                                            row.videosCompleted
                                                        )}{" "}
                                                        done)
                                                    </span>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                                <TableFooter>
                                    <TableRow>
                                        <TableCell className="text-left">
                                            All categories
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {num(data.totals.total)}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {num(data.totals.eligible)}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {num(data.totals.ready)}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {num(data.totals.skippedForImages)}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {num(data.totals.ineligible)}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {num(data.totals.vocabularyMismatch)}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            -
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {num(data.totals.videosGenerated)}
                                        </TableCell>
                                    </TableRow>
                                </TableFooter>
                            </Table>
                        </CardContent>
                    </Card>

                    <p className="text-left text-xs text-muted-foreground">
                        Photo floor: {data.imageFloor} campaign-qualified photos
                        (not deleted, not excluded, not flagged unsuitable by the
                        vision pass). Ready, Under photo floor and Not eligible
                        account for every listing; Tag mismatch is an overlay on
                        Ready, not a fourth bucket. Computed{" "}
                        {format(new Date(data.generatedAt), "PPpp")}.
                    </p>
                </>
            )}
        </div>
    );
};

const SummaryCard: React.FC<{
    title: string;
    value: string;
    note: string;
    emphasis?: boolean;
}> = ({ title, value, note, emphasis }) => (
    <Card className="mt-0 flex flex-col">
        <CardHeader className="p-4 pb-1">
            <CardTitle className="text-left text-sm font-semibold">
                {title}
            </CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0 text-left">
            <div
                className={cn(
                    "text-2xl font-bold",
                    emphasis && "text-[#fa9600]"
                )}
            >
                {value}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">{note}</div>
        </CardContent>
    </Card>
);

export default CommercialAutoVideo;
